// Office Interop's `ColorFormat.RGB` is a Win32 COLORREF:
// red is the low byte and blue is the high byte.
export function numericRgbToHexColor(value: number): string | undefined {
  if (!Number.isFinite(value) || value < 0) return undefined;
  const truncated = Math.trunc(value) >>> 0;
  const r = truncated & 0xff;
  const g = (truncated >> 8) & 0xff;
  const b = (truncated >> 16) & 0xff;
  const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
  return `#${hex.toUpperCase()}`;
}

const NON_EXPLICIT_COLOR_TYPES = new Set([
  "mixed",
  "nofill",
  "none",
  "background",
  "auto",
]);

export function pickFirstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

export function extractColorToken(raw: unknown): string | undefined {
  if (typeof raw === "string" && raw.trim().length > 0) return raw;
  if (typeof raw === "number") return numericRgbToHexColor(raw);
  if (!raw || typeof raw !== "object") return undefined;

  const obj = raw as Record<string, unknown>;
  if (typeof obj.type === "string") {
    const normalizedType = obj.type.trim().toLowerCase();
    if (NON_EXPLICIT_COLOR_TYPES.has(normalizedType)) return undefined;
  }

  const directString = pickFirstNonEmptyString(
    obj.foregroundColor,
    obj.color,
    obj.rgb,
    obj.hex
  );
  if (directString) return directString;

  if (typeof obj.rgb === "number") {
    const fromNumeric = numericRgbToHexColor(obj.rgb);
    if (fromNumeric) return fromNumeric;
  }
  if (typeof obj.color === "number") {
    const fromNumeric = numericRgbToHexColor(obj.color);
    if (fromNumeric) return fromNumeric;
  }

  if (typeof obj.foreColor === "string" && obj.foreColor.trim().length > 0) {
    return obj.foreColor;
  }
  if (obj.foreColor && typeof obj.foreColor === "object") {
    const nested = extractColorToken(obj.foreColor);
    if (nested) return nested;
  }
  if (obj.fill && typeof obj.fill === "object") {
    const nested = extractColorToken(obj.fill);
    if (nested) return nested;
  }

  return undefined;
}

export function normalizeExtractedColorToken(raw: unknown): string | undefined {
  const token = extractColorToken(raw);
  if (!token) return undefined;

  const trimmed = token.trim();
  let rawHex = trimmed.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(rawHex)) {
    rawHex = rawHex
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }
  if (/^[0-9a-f]{8}$/i.test(rawHex)) {
    rawHex = rawHex.slice(0, 6);
  }
  return /^[0-9a-f]{6}$/i.test(rawHex) ? `#${rawHex.toUpperCase()}` : trimmed;
}

export const HOST_BORDER_WEIGHT_UNSET = -2147483648;
const HOST_BORDER_WEIGHT_UNSET_TOLERANCE = 16;

function isHostUnsetBorderWeight(value: number): boolean {
  return Math.abs(value - HOST_BORDER_WEIGHT_UNSET) <= HOST_BORDER_WEIGHT_UNSET_TOLERANCE;
}

export function extractBorderWeight(raw: unknown): number | undefined {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || isHostUnsetBorderWeight(raw)) return undefined;
    return raw;
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if ("weight" in obj) return extractBorderWeight(obj.weight);
  }
  return undefined;
}

export function extractExplicitBorderColor(raw: unknown): string | undefined {
  if (!raw || typeof raw !== "object") return undefined;

  const obj = raw as Record<string, unknown>;
  if (obj.visible === false) return undefined;

  const normalizedStyle =
    typeof obj.style === "string" && obj.style.trim().length > 0
      ? obj.style.trim().toLowerCase()
      : undefined;
  if (normalizedStyle === "mixed") return undefined;

  const normalizedDashStyle =
    typeof obj.dashStyle === "string" && obj.dashStyle.trim().length > 0
      ? obj.dashStyle.trim().toLowerCase()
      : undefined;
  if (normalizedDashStyle === "mixed") return undefined;

  if ("weight" in obj) {
    const weight = extractBorderWeight(obj.weight);
    if (typeof obj.weight === "number" && weight === undefined) return undefined;
  }

  return extractColorToken(obj);
}
